import React, { useState, useEffect } from "react";
import { Box, Stack, Text } from "@mantine/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

export interface ContentBlockTypeCountUpProps {
  /** Start date and time in UTC format (e.g., "2025-11-02T17:00:00Z") */
  startDateTime: string;

  /** Header text to display above the counter */
  headerText?: string;

  /** Footer text to display below the counter */
  footerText?: string;

  /** Additional CSS class name for styling */
  className?: string;

  /** Theme configuration object */
  themeConfig?: any;
}

export const ContentBlockTypeCountUp: React.FC<
  ContentBlockTypeCountUpProps
> = ({ startDateTime, headerText, footerText, className, themeConfig }) => {
  const [daysElapsed, setDaysElapsed] = useState<number>(0);

  useEffect(() => {
    const calculateDaysElapsed = () => {
      // Parse start date as UTC and convert to local timezone
      const startDate = dayjs.utc(startDateTime).local();
      const now = dayjs();

      // If current time is before start time, show 0
      if (now.isBefore(startDate)) {
        setDaysElapsed(0);
      } else {
        // Calculate milliseconds elapsed
        const msElapsed = now.diff(startDate);
        // Convert to days and add 1 (counting starts at 1)
        const days = Math.floor(msElapsed / (24 * 60 * 60 * 1000)) + 1;
        setDaysElapsed(days);
      }

      // Calculate next update time
      const startLocal = dayjs.utc(startDateTime).local();
      const now2 = dayjs();

      let nextUpdateMs: number;

      if (now2.isBefore(startLocal)) {
        // Before start: update when we reach start time
        nextUpdateMs = startLocal.diff(now2);
      } else {
        // After start: update at the next day boundary (relative to start time)
        const nextDayBoundary = startLocal.add(
          Math.floor(now2.diff(startLocal) / (24 * 60 * 60 * 1000)) + 1,
          "day"
        );
        nextUpdateMs = nextDayBoundary.diff(now2);
      }

      // Add a small buffer to ensure we're past the boundary
      return setTimeout(calculateDaysElapsed, nextUpdateMs + 100);
    };

    const timeoutId = calculateDaysElapsed();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [startDateTime]);

  return (
    <Box
      className={`content-block-countup ${className || ""}`}
      h="100%"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--mantine-spacing-lg)",
        ...(themeConfig?.contentBlocks?.countup?.container && {
          ...themeConfig?.contentBlocks?.countup?.container,
        }),
      }}
    >
      <Stack align="center" gap="md" style={{ width: "100%" }}>
        {headerText && (
          <Text
            className="content-block-countup__header"
            style={{
              fontSize: "clamp(0.875rem, 2vw, 1.5rem)",
              fontWeight: 600,
              color: "var(--mantine-color-dimmed)",
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              textAlign: "center",
              marginBottom: "var(--mantine-spacing-xs)",
              ...(themeConfig?.contentBlocks?.countup?.header && {
                ...themeConfig?.contentBlocks?.countup?.header,
              }),
            }}
          >
            {headerText}
          </Text>
        )}

        <Stack
          align="center"
          gap={4}
          className="content-block-countup__counter"
        >
          <Text
            className="content-block-countup__value"
            style={{
              letterSpacing: "-0.03em",
              fontVariantNumeric: "tabular-nums",
              ...(themeConfig?.contentBlocks?.countup?.value && {
                ...themeConfig?.contentBlocks?.countup?.value,
              }),
            }}
          >
            {daysElapsed}
          </Text>
          <Text
            className="content-block-countup__label"
            style={{
              textAlign: "center",
              letterSpacing: "0.1em",
              ...(themeConfig?.contentBlocks?.countup?.label && {
                ...themeConfig?.contentBlocks?.countup?.label,
              }),
            }}
          >
            {daysElapsed === 1 ? "Day" : "Days"}
          </Text>
        </Stack>

        {footerText && (
          <Text
            className="content-block-countup__footer"
            style={{
              fontSize: "clamp(0.875rem, 2vw, 1.5rem)",
              fontWeight: 600,
              color: "var(--mantine-color-dimmed)",
              fontFamily:
                "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              textAlign: "center",
              marginTop: "var(--mantine-spacing-xs)",
              ...(themeConfig?.contentBlocks?.countup?.footer && {
                ...themeConfig?.contentBlocks?.countup?.footer,
              }),
            }}
          >
            {footerText}
          </Text>
        )}
      </Stack>
    </Box>
  );
};
